/**
 * useRagManagement
 *
 * Handles RAG status polling and prepare RAG actions for Knowledge Base entities.
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../components/ToastProvider';
import { ragService, type RAGStatusResponse, type RAGStatsResponse } from '../services/ragService';

export interface UseRagManagementResult {
  ragStatus: RAGStatusResponse | null;
  ragStats: RAGStatsResponse | null;
  ragPolling: boolean;
  ragPrepareLoading: boolean;
  showRagCostDialog: boolean;
  setShowRagCostDialog: React.Dispatch<React.SetStateAction<boolean>>;
  handlePrepareRag: () => void;
  executePrepareRag: () => Promise<void>;
}

export function useRagManagement(
  entityType: string,
  entityId: string | undefined,
  canViewDocuments: boolean,
  kbClass?: string | null,
): UseRagManagementResult {
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [ragStatus, setRagStatus] = useState<RAGStatusResponse | null>(null);
  const [ragStats, setRagStats] = useState<RAGStatsResponse | null>(null);
  const [ragPolling, setRagPolling] = useState(false);
  const [ragPrepareLoading, setRagPrepareLoading] = useState(false);
  const [showRagCostDialog, setShowRagCostDialog] = useState(false);

  // RAG status polling effect (KB only)
  useEffect(() => {
    if (entityType !== 'kb' || !entityId) return;

    if (!canViewDocuments) {
      setRagStatus(null);
      setRagStats(null);
      setRagPolling(false);
      return;
    }

    let intervalId: number | undefined;
    let cancelled = false;

    const shouldContinuePolling = (s: RAGStatusResponse | null): boolean => {
      if (!s) return false;
      if (s.status === 'preparing') return true;
      if (s.vector_index_status === 'in_progress') return true;
      if (s.bm25_index_status === 'in_progress') return true;
      return false;
    };

    const pollStatus = async () => {
      if (cancelled || !entityId) return;
      try {
        const [status, stats] = await Promise.all([
          ragService.status(entityId),
          ragService.stats(entityId).catch(() => null),
        ]);
        if (!cancelled) {
          setRagStatus(status);
          if (stats) setRagStats(stats);
          if (!shouldContinuePolling(status)) {
            setRagPolling(false);
            if (intervalId) clearInterval(intervalId);
          }
        }
      } catch (err: unknown) {
        const e = err as { response?: { status?: number } };
        if (!cancelled && e?.response?.status === 403) {
          setRagPolling(false);
          if (intervalId) clearInterval(intervalId);
        }
      }
    };

    pollStatus();
    if (ragPolling) {
      intervalId = window.setInterval(pollStatus, 3000);
    }

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [entityType, entityId, ragPolling, canViewDocuments]);

  const handlePrepareRag = useCallback(() => {
    if (entityType !== 'kb' || !entityId) return;
    // LKBs are not prepared via the SKB chunk pipeline: always route to the RAG status page,
    // which hosts the SysAdmin LKB (re)build + live status.
    if (kbClass === 'reference_large') {
      navigate(`/kb-rag-status/${entityId}`);
      return;
    }
    const status = ragStatus?.status;
    if (status === 'ready' || status === 'failed') {
      navigate(`/kb-rag-status/${entityId}`);
    } else {
      setShowRagCostDialog(true);
    }
  }, [entityType, entityId, ragStatus, navigate, kbClass]);

  const executePrepareRag = useCallback(async () => {
    if (entityType !== 'kb' || !entityId) return;
    setRagPrepareLoading(true);
    try {
      const res = await ragService.prepare(entityId, { force_reindex: false });
      addToast({ type: 'info', title: 'RAG preparation started', message: res.message || 'Preparing RAG in background...' });
      navigate(`/kb-rag-status/${entityId}`);
    } catch (err: unknown) {
      const e = err as { response?: { status?: number }; message?: string };
      const statusCode = e?.response?.status;
      if (statusCode === 403) {
        addToast({ type: 'error', title: 'Forbidden', message: 'You do not have permission to prepare RAG for this knowledge base.' });
      } else {
        addToast({ type: 'error', title: 'Prepare failed', message: e?.message || 'Failed to start RAG preparation.' });
      }
    } finally {
      setRagPrepareLoading(false);
    }
  }, [entityType, entityId, addToast, navigate]);

  return {
    ragStatus,
    ragStats,
    ragPolling,
    ragPrepareLoading,
    showRagCostDialog,
    setShowRagCostDialog,
    handlePrepareRag,
    executePrepareRag,
  };
}
